import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StatusBadge from "../components/StatusBadge";
import imageCompression from 'browser-image-compression';
import whatsappIcon from '../assets/whatsapp.png';
import undoIcon from '../assets/undo.png';

export default function ViewOrders() {
  const navigate = useNavigate();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' 
      ? 'https://teenagefashion.in/backend/api' 
      : 'http://localhost/newfashion/backend/api');
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");

  // FILTER STATES
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState(location.state?.selectedDate || "");

  // State for sorting
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOrder, setModalOrder] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [alert, setAlert] = useState(location.state?.alert || { type: "", message: "" });
  const [originalEditOrder, setOriginalEditOrder] = useState(null);
  const quantityInputRef = useRef(null);

  // Add state for image editing
  const [editImagePreview, setEditImagePreview] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [sentOrderIds, setSentOrderIds] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('sentOrderIds');
    return stored ? JSON.parse(stored) : [];
  });

  // For green (Completed) WhatsApp button
  const [sentCompletedOrderIds, setSentCompletedOrderIds] = useState(() => {
    const stored = localStorage.getItem('sentCompletedOrderIds');
    return stored ? JSON.parse(stored) : [];
  });

  // Keep localStorage in sync when sentOrderIds changes
  useEffect(() => {
    localStorage.setItem('sentOrderIds', JSON.stringify(sentOrderIds));
  }, [sentOrderIds]);

  useEffect(() => {
    localStorage.setItem('sentCompletedOrderIds', JSON.stringify(sentCompletedOrderIds));
  }, [sentCompletedOrderIds]);

  useEffect(() => {
    setLoading(true);
    fetch(
      `${API_URL}/orders.php?search=${encodeURIComponent(search)}&status=${encodeURIComponent(statusFilter)}&delivery_date=${encodeURIComponent(dateFilter)}`,
      { credentials: 'include' }
    )
      .then((response) => response.json())
      .then((data) => {
        setOrders(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching orders:", error);
        setLoading(false);
      });
  }, [search, statusFilter, dateFilter]);

  useEffect(() => {
    if (alert.message) {
      const timer = setTimeout(() => setAlert({ type: "", message: "" }), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Filter and sort orders
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = orders.filter(order => {
      const matchesSearch = 
        (order.customer_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (order.customer_phone || "").toLowerCase().includes(search.toLowerCase()) ||
        order.fabric_type.toLowerCase().includes(search.toLowerCase()) ||
        order.color.toLowerCase().includes(search.toLowerCase()) ||
        order.id.toString().includes(search);
      
      const matchesStatus = statusFilter === "all" || order.status === statusFilter;
      const matchesDate = !dateFilter || order.delivery_date === dateFilter;
      
      return matchesSearch && matchesStatus && matchesDate;
    });

    // Sort orders
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle date sorting
      if (sortField === "created_at" || sortField === "delivery_date") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }
      
      // Handle numeric sorting
      if (sortField === "total_amount" || sortField === "paid" || sortField === "balance" || sortField === "quantity") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      
      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [orders, search, statusFilter, dateFilter, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleEditOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    setEditOrder({ ...order });
    // Store original order data for comparison
    setOriginalEditOrder(JSON.parse(JSON.stringify(order)));
    setEditModalOpen(true);
    setModalOpen(false); // Close details modal if open
  };

  const handleEditChange = (e) => {
    const { name, value, type } = e.target;
    setEditOrder(prev => {
      let updated = { ...prev, [name]: value };

      // Parse numeric fields as numbers
      if (["total_amount", "paid", "balance", "quantity"].includes(name)) {
        if (name === "quantity") {
          // Only allow positive integers, default to 1 if invalid
          let intVal = parseInt(value, 10);
          if (isNaN(intVal) || intVal < 1) intVal = 1;
          updated[name] = intVal;
        } else {
          updated[name] = value === "" ? 0 : Number(value);
        }
      }

      // Automatically update balance if total_amount or paid changes
      if (name === "total_amount" || name === "paid") {
        const total = name === "total_amount" ? Number(value) : Number(updated.total_amount);
        const paid = name === "paid" ? Number(value) : Number(updated.paid);
        updated.balance = (isNaN(total) ? 0 : total) - (isNaN(paid) ? 0 : paid);
      }

      return updated;
    });
  };

  const handleEditSave = async () => {
    // Get the latest value from the input
    const latestQuantity = quantityInputRef.current ? parseInt(quantityInputRef.current.value, 10) : editOrder.quantity;
    const updatedOrder = { ...editOrder, quantity: isNaN(latestQuantity) || latestQuantity < 1 ? 1 : latestQuantity };

    // Check if any changes were made
    if (!hasOrderDataChanged() && !editImageFile && !removeImage) {
      setAlert({ type: "success", message: "No changes detected. Closing modal..." });
      setTimeout(() => {
        setEditModalOpen(false);
        setEditOrder(null);
        setOriginalEditOrder(null);
        setEditImageFile(null);
        setEditImagePreview(null);
        setRemoveImage(false);
      }, 1000);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('id', updatedOrder.id);
      formData.append('delivery_date', updatedOrder.delivery_date);
      formData.append('status', updatedOrder.status);
      formData.append('total_amount', updatedOrder.total_amount);
      formData.append('paid', updatedOrder.paid);
      formData.append('balance', updatedOrder.balance);
      formData.append('quantity', updatedOrder.quantity);
      formData.append('fabric_type', updatedOrder.fabric_type);
      formData.append('color', updatedOrder.color);
      formData.append('note', updatedOrder.note);
      if (removeImage) {
        formData.append('remove_image', 'true');
      }
      if (editImageFile) {
        // Compress before upload
        try {
          const compressedFile = await imageCompression(editImageFile, {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1024,
            useWebWorker: true
          });
          formData.append('image', compressedFile, compressedFile.name);
        } catch (err) {
          setAlert({ type: "error", message: "Image compression failed. Please try again." });
          return;
        }
      }
      const response = await fetch(`${API_URL}/update_order.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const result = await response.json();
      if (result.success) {
        // Optionally, refetch orders from the backend or update state locally
        setOrders((prev) =>
          prev.map((o) => (o.id === updatedOrder.id ? { ...updatedOrder, image_path: result.image_path } : o))
        );
        setEditModalOpen(false);
        setEditOrder(null);
        setOriginalEditOrder(null);
        setEditImageFile(null);
        setEditImagePreview(null);
        setRemoveImage(false);
        setAlert({ type: "success", message: "Order updated successfully!" });
      } else {
        setAlert({ type: "error", message: result.error || "Failed to update order." });
      }
    } catch (err) {
      setAlert({ type: "error", message: "Failed to update order." });
    }
  };

  const handleEditCancel = () => {
    setEditModalOpen(false);
    setEditOrder(null);
    setOriginalEditOrder(null);
  };

  // Quick status update function
  const handleQuickStatusUpdate = async (orderId, newStatus) => {
    try {
      // Create FormData to match the backend's expected format
      const formData = new FormData();
      formData.append('id', orderId);
      formData.append('status', newStatus);
      
      const response = await fetch(`${API_URL}/update_order.php`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Update the order in local state
        setOrders(prev => 
          prev.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatus }
              : order
          )
        );
        setAlert({ type: "success", message: `Order #${orderId} status updated to ${newStatus}` });
      } else {
        setAlert({ type: "error", message: result.error || "Failed to update status." });
      }
    } catch (err) {
      setAlert({ type: "error", message: "Failed to update status." });
    }
  };

  const hasOrderDataChanged = () => {
    if (!originalEditOrder || !editOrder) return false;
    
    const fieldsToCompare = [
      'delivery_date', 'status', 'total_amount', 'paid', 'balance', 
      'quantity', 'fabric_type', 'color', 'note'
    ];
    
    for (const field of fieldsToCompare) {
      if (originalEditOrder[field] !== editOrder[field]) {
        return true;
      }
    }
    
    return false;
  };

  const handleDeleteOrder = (orderId) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      fetch(`${API_URL}/delete_order.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: orderId }),
        credentials: 'include'
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            // Optionally, refetch orders from the backend or update state locally
            setOrders((prev) => prev.filter((order) => order.id !== orderId));
            setAlert({ type: "success", message: "Order deleted successfully!" });
          } else {
            setAlert({ type: "error", message: data.error || "Failed to delete order." });
          }
        })
        .catch(() => {
          setAlert({ type: "error", message: "Failed to delete order." });
        });
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="text-gray-400">↕</span>;
    return sortDirection === "asc" ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(inputValue);
  };

  const handleClear = () => {
    setInputValue("");
    setSearch("");
    setStatusFilter("all");
    setDateFilter("");
  };

  const handleArchive = () => {
    // Implementation of archive logic
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleDateChange = (e) => {
    setDateFilter(e.target.value);
  };

  // Add handler for image change
  const handleEditImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditImageFile(file);
      setEditImagePreview(URL.createObjectURL(file));
      setRemoveImage(false);
    }
  };
  // Add handler for removing image
  const handleRemoveEditImage = () => {
    setEditImageFile(null);
    setEditImagePreview(null);
    setRemoveImage(true);
  };

  const getImageUrl = (image_path) => {
    if (!image_path) return null;
    if (image_path.startsWith("http")) return image_path;
    return `${process.env.NODE_ENV === "production" ? "https://teenagefashion.in" : "http://localhost/newfashion"}/${image_path}`;
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto w-full">
      {/* Modal for mobile order details */}
      {modalOpen && modalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 modal-open-block-swipe">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
              onClick={() => setModalOpen(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-lg font-bold mb-4">Order Details</h2>
            {/* Show order image if exists */}
            {modalOrder.image_path && (
              <div className="mb-4 flex justify-center">
                <img
                  src={getImageUrl(modalOrder.image_path)}
                  alt="Order"
                  className="w-32 h-32 object-cover rounded border cursor-pointer hover:scale-105 transition-transform"
                  onClick={() => setPreviewUrl(getImageUrl(modalOrder.image_path))}
                />
              </div>
            )}
            <div className="space-y-2 text-sm">
              <div><span className="font-semibold">Order ID:</span> #{modalOrder.id}</div>
              <div><span className="font-semibold">Customer:</span> {modalOrder.customer_name}</div>
              <div><span className="font-semibold">Phone:</span> {modalOrder.customer_phone}</div>
              <div><span className="font-semibold">Delivery Date:</span> {formatDate(modalOrder.delivery_date)}</div>
              <div><span className="font-semibold">Status:</span> <StatusBadge status={modalOrder.status} /></div>
              <div><span className="font-semibold">Total Amount:</span> {formatCurrency(modalOrder.total_amount)}</div>
              <div><span className="font-semibold">Paid:</span> {formatCurrency(modalOrder.paid)}</div>
              <div><span className="font-semibold">Balance:</span> {formatCurrency(modalOrder.balance)}</div>
              <div><span className="font-semibold">Quantity:</span> {modalOrder.quantity}</div>
              <div><span className="font-semibold">Fabric Type:</span> {modalOrder.fabric_type}</div>
              <div><span className="font-semibold">Color:</span> {modalOrder.color}</div>
              <div><span className="font-semibold">Note:</span> {modalOrder.note || "-"}</div>
              <div><span className="font-semibold">Created:</span> {formatDate(modalOrder.created_at)}</div>
            </div>
          </div>
        </div>
      )}
      {/* Image Preview Modal */}
      {previewUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80"
          onClick={() => setPreviewUrl(null)}
        >
          <div className="relative max-w-xs md:max-w-lg w-full p-2" onClick={e => e.stopPropagation()}>
            <button
              className="absolute top-2 right-2 text-white text-2xl bg-black bg-opacity-40 rounded-full w-8 h-8 flex items-center justify-center focus:outline-none"
              onClick={() => setPreviewUrl(null)}
              aria-label="Close preview"
            >
              &times;
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-auto max-h-[80vh] rounded shadow-lg border-2 border-white"
            />
          </div>
        </div>
      )}
      {/* Edit Modal for order */}
      {editModalOpen && editOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 modal-open-block-swipe">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-0 relative">
            <div className="p-6 max-h-[80vh] overflow-y-auto">
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                onClick={handleEditCancel}
                aria-label="Close"
              >
                &times;
              </button>
              <h2 className="text-lg font-bold mb-4">Edit Order</h2>
              <form className="space-y-3" onSubmit={e => { e.preventDefault(); handleEditSave(); }}>
                <div><span className="font-semibold">Order ID:</span> #{editOrder.id}</div>
                {/* Image Edit Section */}
                <div className="mb-3">
                  <label className="block text-gray-700 font-bold mb-2">Order Image</label>
                  {/* Show current image if exists and not removed, or preview if new image selected */}
                  {editImagePreview ? (
                    <div className="flex flex-col items-start gap-2">
                      <img src={editImagePreview} alt="Preview" className="w-32 h-32 object-cover rounded border" />
                      <button type="button" onClick={handleRemoveEditImage} className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">Remove / Retry</button>
                    </div>
                  ) : (editOrder.image_path && !removeImage) ? (
                    <div className="flex flex-col items-start gap-2">
                      <img src={getImageUrl(editOrder.image_path)} alt="Order" className="w-32 h-32 object-cover rounded border" />
                      <button type="button" onClick={handleRemoveEditImage} className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">Remove</button>
                    </div>
                  ) : (
                    <input type="file" accept="image/*" capture="environment" onChange={handleEditImageChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Customer Name</label>
                  <input name="customer_name" value={editOrder.customer_name} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input name="customer_phone" value={editOrder.customer_phone} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" disabled />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Date</label>
                  <input type="date" name="delivery_date" value={editOrder.delivery_date} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select name="status" value={editOrder.status} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Delivered">Delivered</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Total Amount</label>
                  <input name="total_amount" type="number" step="0.01" value={editOrder.total_amount} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Paid</label>
                  <input name="paid" type="number" step="0.01" value={editOrder.paid} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Balance</label>
                  <input name="balance" type="number" step="0.01" value={editOrder.balance} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Quantity</label>
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => {
                        const newQuantity = Math.max(1, editOrder.quantity - 1);
                        setEditOrder(prev => ({ ...prev, quantity: newQuantity }));
                      }}
                      className="bg-gray-100 hover:bg-gray-200 px-4 py-2 text-lg font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[60px]"
                    >
                      -
                    </button>
                    <input
                      name="quantity"
                      type="number"
                      min="1"
                      value={editOrder.quantity}
                      onChange={handleEditChange}
                      ref={quantityInputRef}
                      className="w-full px-3 py-2 text-center border-0 focus:outline-none focus:ring-0"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newQuantity = editOrder.quantity + 1;
                        setEditOrder(prev => ({ ...prev, quantity: newQuantity }));
                      }}
                      className="bg-gray-100 hover:bg-gray-200 px-4 py-2 text-lg font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[60px]"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Fabric Type</label>
                  <input name="fabric_type" value={editOrder.fabric_type} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <input name="color" value={editOrder.color} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Note</label>
                  <input name="note" value={editOrder.note} onChange={handleEditChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
                <div className="flex gap-2 pt-2">
                  <button type="button" onClick={handleEditCancel} className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm font-medium">Cancel</button>
                  <button type="submit" className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium">Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">View Orders</h1>
              <p className="text-gray-600">Manage and track all customer orders</p>
            </div>
            <button
              onClick={() => navigate("/create-order")}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            >
              Create New Order
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {/* Search */}
          <div className="flex flex-col">
            <label htmlFor="search" className="mb-1 font-medium text-gray-700">Search</label>
            <div className="flex">
              <input
                id="search"
                type="text"
                placeholder="Search by customer name"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                className="h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-full"
                style={{ fontSize: "1rem" }}
              />
              <button
                type="button"
                onClick={handleSearch}
                className="ml-2 h-10 px-4 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition"
              >
                Search
              </button>
            </div>
          </div>
          {/* Status */}
          <div className="flex flex-col">
            <label htmlFor="status" className="mb-1 font-medium text-gray-700">Status</label>
            <select
              id="status"
              className="h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-full"
              value={statusFilter}
              onChange={handleStatusChange}
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Delivered">Delivered</option>
            </select>
          </div>
          {/* Delivery Date */}
          <div className="flex flex-col">
            <label htmlFor="deliveryDate" className="mb-1 font-medium text-gray-700">Delivery Date</label>
            <input
              id="deliveryDate"
              type="date"
              className="h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition w-full"
              value={dateFilter}
              onChange={handleDateChange}
            />
          </div>
          {/* Clear Button */}
          <div className="flex flex-col justify-end">
            <button
              type="button"
              onClick={handleClear}
              className="h-8 px-3 bg-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-300 transition w-full"
              style={{ fontSize: "0.95rem" }}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredAndSortedOrders.length} of {orders.length} orders
          </p>
        </div>

        {/* Alert */}
        {alert.message && (
          <div className={`mb-4 p-3 rounded ${alert.type === "success" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {alert.message}
          </div>
        )}

        {/* Orders Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-3 px-2 font-medium hidden md:table-cell cursor-pointer hover:text-gray-700" onClick={() => handleSort("id")}>
                  <div className="flex items-center gap-1">Order ID <SortIcon field="id" /></div>
                </th>
                <th className="py-3 px-2 font-medium">Customer</th>
                <th className="py-3 px-2 font-medium">Status</th>
                <th className="py-3 px-2 font-medium hidden md:table-cell cursor-pointer hover:text-gray-700" onClick={() => handleSort("delivery_date")}> <div className="flex items-center gap-1"> Delivery Date <SortIcon field="delivery_date" /> </div> </th>
                <th className="py-3 px-2 font-medium hidden md:table-cell cursor-pointer hover:text-gray-700" onClick={() => handleSort("total_amount")}> <div className="flex items-center gap-1"> Total Amount <SortIcon field="total_amount" /> </div> </th>
                <th className="py-3 px-2 font-medium hidden md:table-cell cursor-pointer hover:text-gray-700" onClick={() => handleSort("balance")}> <div className="flex items-center gap-1"> Balance <SortIcon field="balance" /> </div> </th>
                <th className="py-3 px-2 font-medium hidden md:table-cell">Details</th>
                <th className="py-3 px-2 font-medium hidden md:table-cell cursor-pointer hover:text-gray-700" onClick={() => handleSort("created_at")}> <div className="flex items-center gap-1"> Created <SortIcon field="created_at" /> </div> </th>
                <th className="py-3 px-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedOrders.map((order) => (
                <tr key={order.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                  <td className="py-3 px-2 text-gray-900 font-medium hidden md:table-cell">#{order.id}</td>
                  <td className="py-3 px-2">
                    {/* Mobile: Show Order ID as a small badge above customer name */}
                    <span className="md:hidden block text-xs text-gray-500 mb-1">#{order.id}</span>
                    <div className="md:hidden">
                      <button
                        className="font-medium text-blue-700 hover:underline focus:outline-none"
                        onClick={() => { setModalOrder(order); setModalOpen(true); }}
                      >
                        {order.customer_name}
                      </button>
                      <div className="text-xs text-gray-500">{order.customer_phone}</div>
                    </div>
                    <div className="hidden md:block">
                      <div className="font-medium text-gray-900">{order.customer_name}</div>
                      <div className="text-xs text-gray-500">{order.customer_phone}</div>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <select
                      value={order.status}
                      onChange={(e) => handleQuickStatusUpdate(order.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-full border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-300 ${
                        order.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        order.status === 'Delivered' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Delivered">Delivered</option>
                    </select>
                  </td>
                  <td className="py-3 px-2 text-gray-700 hidden md:table-cell">{formatDate(order.delivery_date)}</td>
                  <td className="py-3 px-2 text-gray-900 font-medium hidden md:table-cell">{formatCurrency(order.total_amount)}</td>
                  <td className="py-3 px-2 hidden md:table-cell">
                    <span className={order.balance > 0 ? "text-red-600 font-medium" : "text-green-600 font-medium"}>
                      {formatCurrency(order.balance)}
                    </span>
                  </td>
                  <td className="py-3 px-2 hidden md:table-cell">
                    <div className="text-xs text-gray-600">
                      <div>Qty: {order.quantity}</div>
                      <div>{order.fabric_type} - {order.color}</div>
                    </div>
                  </td>
                  <td className="py-3 px-2 text-gray-500 text-xs hidden md:table-cell">{formatDate(order.created_at)}</td>
                  <td className="py-3 px-2">
                    <div className="flex gap-2 items-center justify-start">
                      <button
                        onClick={() => handleEditOrder(order.id)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteOrder(order.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                      {/* WhatsApp button for Completed orders */}
                      {order.status === "Completed" && order.customer_phone && (
                        <div className="relative flex items-center gap-1 justify-start" style={{ minWidth: '2rem' }}>
                          <button
                            type="button"
                            disabled={sentCompletedOrderIds.includes(order.id)}
                            onClick={() => {
                              window.open(
                                `https://wa.me/${order.customer_phone.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hello ${order.customer_name}, your stitching order is now complete and ready for pickup from TeenageFashion, Shoranur. Thank you for choosing us! 😊\n\nAlso, check out our latest arrivals at 👉 https://www.teenagefashion.in`)}`,
                                '_blank',
                                'noopener,noreferrer'
                              );
                              setSentCompletedOrderIds(prev => {
                                const updated = [...prev, order.id];
                                return updated;
                              });
                            }}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-300 ${sentCompletedOrderIds.includes(order.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Notify via WhatsApp"
                            style={{ minWidth: 0, padding: 0 }}
                          >
                            <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4" />
                          </button>
                          {sentCompletedOrderIds.includes(order.id) && (
                            <button
                              type="button"
                              onClick={() => {
                                setSentCompletedOrderIds(prev => prev.filter(id => id !== order.id));
                              }}
                              className="absolute -right-2 -bottom-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 shadow"
                              title="Reset WhatsApp notification"
                              style={{ minWidth: 0, padding: 0 }}
                            >
                              <img src={undoIcon} alt="Reset" className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                      {/* WhatsApp button for Pending orders */}
                      {order.status === "Pending" && (
                        <div className="relative flex items-center gap-1 justify-start" style={{ minWidth: '2rem' }}>
                          <button
                            type="button"
                            disabled={sentOrderIds.includes(order.id)}
                            onClick={() => {
                              window.open(
                                `https://wa.me/91${(order.customer_phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
                                  `Hello ${order.customer_name || ""}, thank you for placing your order with TeenageFashion, Shoranur! 🧵\n\n📱 Phone: ${(order.customer_phone || "")}\n🔐 PIN: ${(order.customer_pin || "")}\n\nUse these credentials to track your order here 👇\nhttps://track.teenagefashion.in\n\nWe'll notify you once your stitching is complete. 😊`
                                )}`,
                                '_blank',
                                'noopener,noreferrer'
                              );
                              setSentOrderIds(prev => {
                                const updated = [...prev, order.id];
                                // localStorage will be updated by useEffect
                                return updated;
                              });
                            }}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-yellow-300 ${sentOrderIds.includes(order.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Notify via WhatsApp (Order Placed)"
                            style={{ minWidth: 0, padding: 0 }}
                          >
                            <img src={whatsappIcon} alt="WhatsApp" className="w-4 h-4" />
                          </button>
                          {sentOrderIds.includes(order.id) && (
                            <button
                              type="button"
                              onClick={() => {
                                setSentOrderIds(prev => prev.filter(id => id !== order.id));
                              }}
                              className="absolute -right-2 -bottom-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 shadow"
                              title="Reset WhatsApp notification"
                              style={{ minWidth: 0, padding: 0 }}
                            >
                              <img src={undoIcon} alt="Reset" className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {filteredAndSortedOrders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-600 mb-4">Try adjusting your search or filters</p>
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("all");
                setDateFilter("");
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
